import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faArrowDown, faArrowUp, faTrash} from "@fortawesome/free-solid-svg-icons";
import LineByLineTextErrorsComponent from "./LineByLineTextErrorsComponent";
import {InvalidArgumentException} from "../../js/errors/Exceptions";


/**
 * Returns id of StateInput in the following format: <code>states-input-item-INDEX</code>
 * @param index {number}
 * @return {string}
 */
export const getStateInputId = (index) => {
    return 'states-input-item-' + index;
}

/**
 * Parses <code>INDEX</code> (as number) from given <code>id</code> that is in required format
 * @param id {string} must be in following format: <code>states-input-item-INDEX</code>
 * @return {number}
 */
export const parseStateInputIndexFromId = (id) => {
    if (!/^states-input-item-\d+$/.test(id)){
        throw new InvalidArgumentException("id must be in the following format: 'states-input-item-INDEX'," +
            "where INDEX integer number >= 0");
    }
    return parseInt(id.match(/\d+/g)[0]);
}


/**
 * Component with inputs that necessary for creating a <code>TmState</code>
 *
 * @param index {number} index of corresponding state (the same with index in order StateInputComponents placed on the screen)
 * @param statesInputsCount {number} number of all states inputs
 * @param stateInput object that contains current values for input fields of corresponding state: <code>
 *                              {name: string,
 *                              description: string,
 *                              final: boolean}
 *                              </code>.
 * @param checkedInitialStateId {string|undefined} is ID (in <code>states-input-item-INDEX</code> format) of current selected initial state.
 * Can be <code>undefined</code>
 * @param inputErrors {TmParsingError[]} errors for current state of input fields
 * @param onNameChanged callback that should be called when <code>name</code> field is changed.
 * It updates useState variable of entire page. Takes <code>newName</code> and <code>index</code>.
 * @param onDescriptionChanged callback that should be called when <code>description</code> field is changed.
 * It updates useState variable of entire page. Takes <code>newDescription</code> and <code>index</code>.
 * @param onInitialChanged callback that should be called when initialState-radiobutton is changed.
 * It updates useState variable of entire page. Takes <code>newInitialStateInputId</code>.
 * @param onFinalChanged callback that should be called when final-checkbox is changed.
 * It updates useState variable of entire page. Takes <code>index</code>.
 * @param onDeleteInput callback that should be called when delete button is clicked.
 * It updates useState variable of entire page - deletes this StateInputComponent. Takes <code>index</code>.
 * @constructor
 */
export default function TmStateInputComponent({index, statesInputsCount,
                                                stateInput,
                                                checkedInitialStateId,
                                                inputErrors,
                                                onNameChanged, onDescriptionChanged,
                                                onInitialChanged, onFinalChanged,
                                                onRaiseUpInput, onPutDownInput, onDeleteInput}){
    // return row with all inputs
    return(
        <div className="states-input-item row m-0 mt-1">

            {/* Name */}
            <div className="col-3">
                <input name='name' type='text' required
                       className={"form-control"}
                       maxLength={5}
                       value={stateInput?.name}
                       onChange={(e) => onNameChanged(e.target.value, index)}
                />

                {/* parsing errors for name input */}
                <LineByLineTextErrorsComponent
                    errors={inputErrors?.filter((error) => error.part === 'state-input-name')}
                />
            </div>

            {/* Description */}
            <div className="col-4">
                <input name='name' type='text' required
                       className={"form-control"}
                       maxLength={100}
                       value={stateInput?.description}
                       onChange={(e) => onDescriptionChanged(e.target.value, index)}
                />

                {/* parsing errors for description input */}
                <LineByLineTextErrorsComponent
                    errors={inputErrors?.filter((error) => error.part === 'state-input-description')}
                />
            </div>

            {/* Is initial */}
            <div className="col-1 text-center align-content-center">
                <input type="radio"
                       name="states-input-item-initial"
                       value={getStateInputId(index)}
                       checked={checkedInitialStateId === getStateInputId(index)} // if equals to value of current radiobutton
                       onChange={(e) => onInitialChanged(e.target.value)} // simply call setState
                />
            </div>

            {/* Is final */}
            <div className="col-1 text-center align-content-center">
                <input type="checkbox"
                       name="states-input-item-final"
                       checked={stateInput ? stateInput.final : false}
                       onChange={(e) => onFinalChanged(index)} // change inputValues.final to the opposite
                />
            </div>

            {/* Up, down, delete buttons */}
            <div className="col-3">
                <div className={'d-flex flex-row justify-content-center'}>
                    <button type="button" className="btn btn-primary m-1 mb-0"
                            disabled={index === 0}
                            onClick={(e) => onRaiseUpInput(index)}>
                        <p className={'m-0 p-0'}><FontAwesomeIcon icon={faArrowUp}/></p>
                    </button>

                    <button type="button" className="btn btn-primary m-1 mb-0"
                            disabled={index === statesInputsCount - 1}
                            onClick={(e) => onPutDownInput(index)}>
                        <p className={'m-0 p-0'}><FontAwesomeIcon icon={faArrowDown}/></p>
                    </button>

                    <button type="button" className="btn btn-danger m-1 mb-0"
                            disabled={statesInputsCount === 1}
                            onClick={(e) => onDeleteInput(index)}>
                        <p className={'m-0 p-0'}><FontAwesomeIcon icon={faTrash}/></p>
                    </button>
                </div>
            </div>
        </div>
    );
}